import { Injectable } from '@angular/core';
import {
  CrmEntity, CrmEntityAttribute, CrmEntityAttributeTypeEnum, CrmEntityTypeEnum
} from './crm-entities.model';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { HttpService } from '../http/http.service';
import { finalize } from 'rxjs/operators';
import { ApiEndpointService } from '../services/api-endpoint.service';

interface IRawParams {
  [key: string]: any;
}

export interface IAttributeOptionData {
  _id: string;
  Text: string;
  Value: string;
}

interface ICrmEntityAttributeData {
  Accuracy: number;
  FullNameMode: boolean;
  _id: string; // the logical name in crm
  Name: string; // the way it should appear on the UI (should be already translated
  LookupEntityIds: any[]; // TODO: check what should be implemented here
  MaxLength: number;
  Options: IAttributeOptionData[];
  Type: CrmEntityAttributeTypeEnum;
}

interface ICrmEntityData {
  Attributes: ICrmEntityAttributeData[];
  EntityType: CrmEntityTypeEnum;
  EntityName: string;
  EntityPluralLabel: string;
}

interface IEntities extends IRawParams {
  Contact: ICrmEntityData;
  Lead: ICrmEntityData;
}

@Injectable()
export class CrmEntitiesService {
  lead: CrmEntity;
  contact: CrmEntity;
  entities: CrmEntity[] = [];
  entitiesNameToEntity: { [name: string]: CrmEntity } = {};
  loadingDataInProcess: BehaviorSubject<boolean> = new BehaviorSubject(true);
  loading = true;

  constructor(private httpService: HttpService, private apiEndpoint: ApiEndpointService) {
    this.getAllEntities();
  }

  buildCrmEntitiesModels(entitiesData: IEntities) {
    Object.keys(entitiesData).forEach((entityName: string) => {
      const entityData: ICrmEntityData = entitiesData[entityName];

      const crmEntity = new CrmEntity();
      crmEntity.entityType = entityData.EntityType;
      crmEntity.entityName = entityData.EntityName;
      crmEntity.entityPluralLabel = entityData.EntityPluralLabel;

      entityData.Attributes.forEach((crmAttributeData: ICrmEntityAttributeData) => {
        const crmEntityAttribute = this.createCrmAttribute(crmAttributeData);
        crmEntity.attributes.push(crmEntityAttribute);
      });

      switch (entityData.EntityType) {
        case CrmEntityTypeEnum.Lead:
          this.lead = crmEntity;
          break;
        case CrmEntityTypeEnum.Contact:
          this.contact = crmEntity;
          break;
        default:
          const entityDataAString = JSON.stringify(entityData);
          console.error(`this should not have happened. we got an entity that we dont know how to handle entityData: ${entityDataAString}`);
      }

      this.entities.push(crmEntity);
      this.entitiesNameToEntity[crmEntity.entityName] = crmEntity;
    });
  }

  getEntityByName(entityName: string) {
    return this.entitiesNameToEntity[entityName];
  }

  getEntityByType(requestedEntityType: CrmEntityTypeEnum) {
    let result;
    this.entities.forEach((entity: CrmEntity) => {
      if (entity.entityType === requestedEntityType) {
        result = entity;
      }
    });

    return result;
  }

  createCrmAttribute(crmAttributeData: ICrmEntityAttributeData) {
    const resultAttribute = new CrmEntityAttribute();

    resultAttribute.type = crmAttributeData.Type;
    resultAttribute.accuracy = crmAttributeData.Accuracy;
    resultAttribute.fullNameMode = crmAttributeData.FullNameMode;
    resultAttribute.logicalName = crmAttributeData._id;
    resultAttribute.lookupEntityIds = crmAttributeData.LookupEntityIds;
    resultAttribute.maxLength = crmAttributeData.MaxLength;
    resultAttribute.name = crmAttributeData.Name;

    if (crmAttributeData.Options) {
      resultAttribute.options = crmAttributeData.Options.map((option: IAttributeOptionData) => {
        const optionToReturn = {
          text: option.Text,
          value: option.Value
        };

        if (resultAttribute.type === 'Boolean') {
          // correct the value in boolean to be the right one to compare to. these are athe values that will be loaded to the
          // xml of the free-marker so we need the right one to compare to.
          optionToReturn.value = option.Value === '0' ? 'false' : 'true';
        }
        return optionToReturn;
      });
    }

    return resultAttribute;
  }

  getAllEntities(): Observable<IEntities> {
    if (this.entities.length > 0) {
      return of();
    }
    const crmEntitiesRequest$ = this.httpService.get(this.apiEndpoint.CrmEntitiesUrl);
      crmEntitiesRequest$.pipe(finalize(() => {
        this.loading = false;
      }))
      .subscribe((entitiesData: IEntities) => {
        this.buildCrmEntitiesModels(entitiesData);
        this.loading = false;
        this.loadingDataInProcess.next(this.loading);
      });

      return crmEntitiesRequest$;
  }
}

